import React, { useState, useMemo } from 'react';
import { useStore } from '../context/StoreContext';
import TransactionModal from '../components/TransactionModal';
import FinancialSummaryCards from '../components/FinancialSummaryCards';
import CashFlowIntelligence from '../components/CashFlowIntelligence';
import RevenueEfficiencyRadar from '../components/RevenueEfficiencyRadar';
import RecurringExpenseManager from '../components/RecurringExpenseManager';
import RecurringRevenueManager from '../components/RecurringRevenueManager';
import PropertyPerformance from '../components/PropertyPerformance';
import AdvancedTransactionFilters from '../components/AdvancedTransactionFilters';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { isWithinInterval, parseISO, startOfDay, endOfDay } from 'date-fns';

const { FiPlus, FiDownload, FiSearch, FiZap, FiLayout, FiActivity, FiPieChart, FiEdit2, FiTrash2 } = FiIcons;

const Financials = () => {
  const { data, addRecord, updateRecord, deleteRecord } = useStore();
  const [activeTab, setActiveTab] = useState('analytics');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [advancedFilters, setAdvancedFilters] = useState({
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
    propertyId: 'all',
    type: 'all',
    category: 'all',
    selectedTags: []
  });

  const filteredTx = useMemo(() => {
    return (data.transactions || []).filter(t => {
      // 1. Basic Search
      const matchesSearch = t.description?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           t.category?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;

      // 2. Date Range
      if (advancedFilters.startDate || advancedFilters.endDate) {
        const txDate = parseISO(t.date);
        const start = advancedFilters.startDate ? startOfDay(parseISO(advancedFilters.startDate)) : new Date(0);
        const end = advancedFilters.endDate ? endOfDay(parseISO(advancedFilters.endDate)) : new Date(8640000000000000);
        if (!isWithinInterval(txDate, { start, end })) return false;
      }

      // 3. Amount Range
      const amount = Number(t.amount);
      if (advancedFilters.minAmount && amount < Number(advancedFilters.minAmount)) return false;
      if (advancedFilters.maxAmount && amount > Number(advancedFilters.maxAmount)) return false;

      // 4. Property & Category
      if (advancedFilters.propertyId !== 'all' && t.propertyId !== advancedFilters.propertyId) return false;
      if (advancedFilters.category !== 'all' && t.category !== advancedFilters.category) return false;

      // 5. Tags
      if (advancedFilters.selectedTags.length > 0) {
        const hasTag = advancedFilters.selectedTags.every(tag => t.tags?.includes(tag));
        if (!hasTag) return false;
      }

      return true;
    });
  }, [data.transactions, searchTerm, advancedFilters]);

  const tabs = [
    { id: 'analytics', label: 'Analytics Dashboard', icon: FiPieChart },
    { id: 'ledger', label: 'Financial Ledger', icon: FiLayout },
    { id: 'automation', label: 'Automation', icon: FiZap },
    { id: 'performance', label: 'Yield metrics', icon: FiActivity }
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">Financial Intelligence</h2>
          <p className="text-sm text-gray-500 font-medium italic">Advanced P&L modeling and portfolio cash flow.</p>
        </div>
        <div className="flex gap-2">
          <button className="flex items-center px-5 py-3 bg-white border border-gray-100 text-gray-700 rounded-2xl hover:bg-gray-50 transition shadow-sm font-black text-sm uppercase tracking-wider">
            <SafeIcon icon={FiDownload} className="mr-2" /> Export
          </button>
          <button onClick={() => { setEditingTransaction(null); setIsModalOpen(true); }} className="flex items-center px-5 py-3 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 font-black text-sm uppercase tracking-wider">
            <SafeIcon icon={FiPlus} className="mr-2" /> Add Entry
          </button>
        </div>
      </div>

      <FinancialSummaryCards transactions={filteredTx} />

      <div className="space-y-4">
        <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-3xl shadow-sm border border-gray-100">
          <div className="flex bg-gray-50 p-1 rounded-2xl overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-6 py-2 text-xs font-black uppercase tracking-tight rounded-xl transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <SafeIcon icon={tab.icon} className="mr-2" size={14} /> {tab.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search descriptions or categories..." 
              className="w-full pl-12 pr-4 py-2.5 bg-gray-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <AdvancedTransactionFilters 
          properties={data.properties}
          categories={[...data.incomeCategories, ...data.expenseCategories]}
          tags={data.transactionTags}
          onFilterChange={setAdvancedFilters}
          onClear={() => setAdvancedFilters({
            startDate: '', endDate: '', minAmount: '', maxAmount: '',
            propertyId: 'all', type: 'all', category: 'all', selectedTags: []
          })}
        />
      </div>

      {activeTab === 'analytics' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <CashFlowIntelligence transactions={filteredTx} />
          <RevenueEfficiencyRadar properties={data.properties} transactions={filteredTx} />
        </div>
      )}

      {activeTab === 'performance' && <PropertyPerformance properties={data.properties} transactions={filteredTx} />}
      {activeTab === 'automation' && (
        <div className="space-y-10">
          <RecurringRevenueManager />
          <div className="border-t border-gray-200 pt-10">
            <RecurringExpenseManager />
          </div>
        </div>
      )}
      
      {activeTab === 'ledger' && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Date</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Details</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Category</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Tags</th>
                  <th className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Amount</th>
                  <th className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredTx.sort((a,b) => new Date(b.date) - new Date(a.date)).map(t => (
                  <tr key={t.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 text-sm font-black text-gray-900">{t.date}</td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-black text-gray-900">{t.description}</p>
                      <p className="text-[10px] text-gray-400 font-bold uppercase">{data.properties.find(p => p.id === t.propertyId)?.name || 'Shared'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${t.type === 'Income' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                        {t.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {t.tags?.map(tag => (
                          <span key={tag} className="text-[8px] font-black text-gray-400 uppercase">#{tag}</span>
                        ))}
                      </div>
                    </td>
                    <td className={`px-6 py-4 text-right font-black ${t.type === 'Income' ? 'text-green-600' : 'text-red-600'}`}>
                      {t.type === 'Income' ? '+' : '-'}${Number(t.amount).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => { setEditingTransaction(t); setIsModalOpen(true); }} title="Edit entry"
                          className="p-1.5 text-gray-300 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors">
                          <SafeIcon icon={FiEdit2} size={14} />
                        </button>
                        <button onClick={() => deleteRecord('transactions', t.id)} title="Delete entry"
                          className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                          <SafeIcon icon={FiTrash2} size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredTx.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-20 text-center">
                      <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <SafeIcon icon={FiSearch} size={24} className="text-gray-200" />
                      </div>
                      <p className="text-xs font-black text-gray-400 uppercase tracking-widest">No entries match your filters</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <TransactionModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingTransaction(null); }}
        onSave={(td) => {
          if (editingTransaction?.id) {
            updateRecord('transactions', editingTransaction.id, td);
          } else {
            addRecord('transactions', td);
          }
          setEditingTransaction(null);
        }}
        initialData={editingTransaction}
        properties={data.properties}
      />
    </div>
  );
};

export default Financials;